import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues,
    EfeResponse,
    Field,
    InformationLegalRepresentativeServiceRequest,
    ScreenFilterValues,
    View,
} from 'commons/models';
import {
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    Messages,
    PersonTypes,
    Roles,
    SnackbarVariants,
    ViewChildId,
    ViewKeys,
} from 'commons/constants';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import RepresentativeOfficialInfoForm from './representative-official-info-form/RepresentativeOfficialInfoForm';
import {nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {ClientService, LayoutServices} from 'services';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {fieldCatalogGetter} from 'hooks/FlowHook';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';

function RepresentativeOfficialInfo() {
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const {flow, updateModuleStep} = useFlowContext();
    const {menu, kycId, fatherKycId} = useMenuContext();
    const [fields, setFields] = React.useState<Array<Field>>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const {selectedRepresentative, tempRepresentative} = useRelatedPeopleContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const [auxLegalRepresentative, setAuxLegalRepresentative] = React.useState<any>(undefined);

    async function onSubmit(values: InformationLegalRepresentativeServiceRequest) {
        try {
            setIsLoading(true);
            const request = {...values};
            request.kycId = kycId;
            const response: EfeResponse<InformationLegalRepresentativeServiceRequest> =
                await ClientService.saveLegalRepresentativeOfficialInformationData(request);
            if (response && response.data) {
                setFields([...fields]);
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
                setFields([...fields]);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    function onhandleChange() {
        history.push(HistoryRoutes.REPRESENTATIVE_OFFICIAL_INFO);
    }

    async function overrideFields(personTypeKey: string, view: View | undefined) {
        if (personTypeKey && view && view.fields) {
            await setFields(view.fields);
        } else {
            await setFields(viewFields);
        }
    }

    async function getFieldVariant() {
        try {
            setIsLoading(true);
            const request: ScreenFilterValues = {
                viewId: flow.data.viewId,
                personType: PersonTypes.FISICA,
                foreign: tempRepresentative?.foreignCitizen || false,
                address: false,
                rol: Roles.LEGAL_REPRESENTATIVE,
                viewKey: ViewChildId.LEGAL_REPRESENTATIVE_OFFICIAL_INFO,
            };
            const response: EfeResponse<View> = await LayoutServices.getViewByFilters(request);
            if (response.data && response.data.fields) {
                setIsLoading(false);
                overrideFields(PersonTypes.FISICA, response.data);
            } else {
                enqueueSnackbar(Messages.LAYOUT_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
                overrideFields(PersonTypes.FISICA, undefined);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            overrideFields(PersonTypes.FISICA, undefined);
            showAlertError(error, Messages.LAYOUT_ERROR);
        }
    }

    React.useEffect(() => {
        if (
            flow.data.id === ViewChildId.LEGAL_REPRESENTATIVE_OFFICIAL_INFO &&
            tempRepresentative?.foreignCitizen
        ) {
            getFieldVariant();
        } else {
            setFields(viewFields);
        }
        if (
            fatherKycId > 0 &&
            kycId > 0 &&
            selectedRepresentative > 0 &&
            ViewKeys.LEGAL_OFFICIAL_INFO === flow.data.expandKey
        ) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<InformationLegalRepresentativeServiceRequest> =
                        await ClientService.getLegalRepresentativeIdentification(kycId);
                    if (response && response.data) {
                        setAuxLegalRepresentative(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setAuxLegalRepresentative(undefined);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Informaci\u00F3n oficial del Representante Legal o Apoderado Legal',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <RepresentativeOfficialInfoForm
                            fields={[
                                ...fieldCatalogGetter(
                                    fields.filter((field) => ATTR_NULL !== field.attrName)
                                ),
                            ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                            submitHandler={onSubmit}
                            auxLegalRepresentative={auxLegalRepresentative}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.LEGAL_OFFICIAL_INFO === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion onChange={onhandleChange} values={accordion} />
        </MainContent>
    );
}

export default RepresentativeOfficialInfo;
